/**
 * MONC XIII — 1-per-sample cart limit (theme-level, soft enforcement).
 * Replaces the Avada Order Limits prompt (app uninstalled 2026-07-21).
 *
 * Lives in the theme as assets/monc-sample-limit.js, included from
 * layout/theme.liquid. A reference copy is kept in the dashboard repo at
 * prd-techspec/theme-snippets/monc-sample-limit.js — keep both in sync.
 *
 * How it works (validated against the live theme 2026-07-21):
 *  - Quantity steppers and qty inputs (cart page + drawer) go through the
 *    Alpine cart store, which POSTs /cart/update.js {updates:{<variantId>:qty}}
 *    via window.fetch, then re-renders from the response. We patch fetch and
 *    clamp sample-variant quantities to 1 pre-flight, so the UI stays
 *    consistent with no reload. (NOTE: this theme's button classes are
 *    swapped — the "+" button has class *__item-decrement.)
 *  - Adding the same sample twice from the product page accumulates the line
 *    to qty 2; we catch it in the /cart/add.js response, correct the line via
 *    /cart/change.js, and reload with the prompt queued.
 *  - A sweep on every page load self-heals anything that slipped through
 *    other paths (XHR adds, other tabs). Shopify POS/draft orders and the
 *    Shop app bypass theme JS entirely — accepted limitation of option B.
 *  - Samples are identified by variant title containing "Sample"
 *    ("2ml - Sample", "1.5ml - Sample", ...). Everything is fail-open: any
 *    error falls through to stock behavior.
 */
(function () {
  var MSG = "Samples are limited to 1 per order.";
  var FLAG = "moncSampleLimitToast";
  var rawFetch = window.fetch.bind(window);
  var sampleVariantIds = {}; // variant id (string) -> true
  var sampleLineKeys = {};   // line key -> true

  function isSample(text) { return /sample/i.test(text || ""); }

  function toast() {
    try {
      var t = document.getElementById("monc-sample-limit-toast");
      if (!t) {
        t = document.createElement("div");
        t.id = "monc-sample-limit-toast";
        t.setAttribute("role", "status");
        t.style.cssText =
          "position:fixed;left:50%;bottom:32px;transform:translateX(-50%);" +
          "background:#000;color:#fff;padding:14px 22px;font-size:14px;" +
          "letter-spacing:.05em;z-index:99999;opacity:0;transition:opacity .3s;" +
          "max-width:90vw;text-align:center;pointer-events:none";
        document.body.appendChild(t);
      }
      t.textContent = MSG;
      // Sync + forced reflow (not rAF): rAF never fires in background tabs.
      void t.offsetHeight;
      t.style.opacity = "1";
      clearTimeout(t._hide);
      t._hide = setTimeout(function () { t.style.opacity = "0"; }, 4000);
    } catch (e) { /* never break the page over a toast */ }
  }

  function learnCart(cart) {
    try {
      if (!cart || !cart.items) return;
      sampleVariantIds = {}; sampleLineKeys = {};
      cart.items.forEach(function (it) {
        if (isSample(it.variant_title) || isSample(it.title)) {
          sampleVariantIds[String(it.variant_id)] = true;
          sampleLineKeys[it.key] = true;
        }
      });
    } catch (e) { /* fail open */ }
  }

  // Reload guard: at most 2 corrective reloads per minute.
  function safeReload() {
    try {
      var now = Date.now();
      var hist = JSON.parse(sessionStorage.getItem("moncSampleLimitReloads") || "[]")
        .filter(function (ts) { return now - ts < 60000; });
      if (hist.length >= 2) return;
      hist.push(now);
      sessionStorage.setItem("moncSampleLimitReloads", JSON.stringify(hist));
      window.location.reload();
    } catch (e) { window.location.reload(); }
  }

  // Self-heal: fix any sample line with qty > 1, prompt, refresh.
  var sweeping = false;
  function sweep(reloadIfFixed) {
    if (sweeping) return;
    sweeping = true;
    rawFetch("/cart.js")
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        learnCart(cart);
        var offenders = (cart.items || []).filter(function (it) {
          return it.quantity > 1 && (isSample(it.variant_title) || isSample(it.title));
        });
        if (!offenders.length) { sweeping = false; return; }
        var fixes = offenders.map(function (it) {
          return rawFetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: it.key, quantity: 1 }),
          });
        });
        Promise.all(fixes).then(function () {
          sweeping = false;
          if (reloadIfFixed) { sessionStorage.setItem(FLAG, "1"); safeReload(); }
          else { toast(); }
        });
      })
      .catch(function () { sweeping = false; });
  }

  window.fetch = function (input, init) {
    // Pre-flight clamp: the stepper/qty-input path (no reload needed).
    try {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      if (/\/cart\/(update|change)(\.js)?/.test(url) && init && typeof init.body === "string") {
        var body = JSON.parse(init.body);
        var clamped = false;
        if (body.updates && typeof body.updates === "object") {
          Object.keys(body.updates).forEach(function (k) {
            if (parseInt(body.updates[k], 10) > 1 &&
                (sampleVariantIds[String(k)] || sampleLineKeys[k])) {
              body.updates[k] = 1; clamped = true;
            }
          });
        } else if (body.id != null && parseInt(body.quantity, 10) > 1 &&
                   (sampleVariantIds[String(body.id)] || sampleLineKeys[body.id])) {
          body.quantity = 1; clamped = true;
        }
        if (clamped) {
          init = Object.assign({}, init, { body: JSON.stringify(body) });
          toast();
        }
      }
    } catch (e) { /* fail open */ }

    var p = rawFetch(input, init);

    // Post-flight: double-add from product page & cart state learning.
    try {
      var u = typeof input === "string" ? input : (input && input.url) || "";
      if (/\/cart\/add(\.js)?/.test(u)) {
        p = p.then(function (res) {
          res.clone().json().then(function (data) {
            var items = data && data.items ? data.items : [data];
            var bad = items.some(function (it) {
              return it && it.quantity > 1 && (isSample(it.variant_title) || isSample(it.title));
            });
            if (bad) sweep(true);
          }).catch(function () {});
          return res;
        });
      } else if (/\/cart(\.js)?(\?|$)/.test(u) || /\/cart\/(update|change)(\.js)?/.test(u)) {
        p = p.then(function (res) {
          res.clone().json().then(learnCart).catch(function () {});
          return res;
        });
      }
    } catch (e) { /* fail open */ }
    return p;
  };

  function init() {
    if (sessionStorage.getItem(FLAG)) {
      sessionStorage.removeItem(FLAG);
      toast();
    }
    sweep(false);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
